/**
 * Product Configurator Services
 * 
 * Shared business logic services used by ProductConfigurator and CartController.
 * All services are static classes that provide stateless utility methods.
 * 
 * Services:
 * - VariantSelector: Variant selection logic for dates/durations
 * - PriceCalculator: Price fetching and calculations  
 * - CartManager: All cart operations (add, update, swap)
 * - ProductDataCache: Product data fetching with caching
 * 
 * Usage:
 *   const variant = await VariantSelector.selectVariantForDates(handle, startDate, endDate);
 *   const price = await PriceCalculator.getPriceForDuration(handle, '7 Day');
 *   await CartManager.addItem(variantId, quantity, properties);
 */

console.log('🔧 js-configurator-services.js LOADED', {
  services: ['VariantSelector', 'PriceCalculator', 'CartManager', 'ProductDataCache']
});

/**
 * ProductDataCache Service
 * 
 * Centralized product data fetching with automatic caching.
 * Uses SessionState for persistent caching across page loads.
 * Reduces redundant API calls and improves performance.
 */
class ProductDataCache {
  /**
   * Get product data (cached in session storage)
   * 
   * @param {string} handle - Product handle
   * @returns {Promise<object>} Product data from Shopify
   * 
   * @example
   * const product = await ProductDataCache.get('air-quality-monitor');
   * console.log(product.variants);
   */
  static async get(handle) {
    // Check session storage cache first
    const cacheKey = `product_${handle}`;
    const cached = window.SessionState?.get(cacheKey);
    
    if (cached) {
      console.log(`📦 ProductDataCache: Using cached data for "${handle}"`);
      return cached;
    }
    
    try {
      console.log(`🌐 ProductDataCache: Fetching data for "${handle}"`);
      const response = await fetch(`/products/${handle}.js`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Store in session storage if available
      if (window.SessionState) {
        window.SessionState.set(cacheKey, data);
      }
      
      console.log(`✅ ProductDataCache: Cached data for "${handle}"`, {
        variants: data.variants.length
      });
      
      return data;
    } catch (error) {
      console.error(`❌ ProductDataCache: Error fetching "${handle}":`, error);
      throw error;
    }
  }
  
  /**
   * Clear cache for specific product or all products
   * 
   * @param {string} [handle] - Optional product handle. If not provided, clears all cache.
   * 
   * @example
   * ProductDataCache.clear('air-quality-monitor'); // Clear one product
   * ProductDataCache.clear(); // Clear all
   */
  static clear(handle = null) {
    if (!window.SessionState) return;
    
    if (handle) {
      const cacheKey = `product_${handle}`;
      window.SessionState.clear(cacheKey);
      console.log(`🧹 ProductDataCache: Cleared cache for "${handle}"`);
    } else {
      // Clear all product_ prefixed keys
      const allKeys = window.SessionState.getAllKeys();
      const productKeys = allKeys.filter(k => k.startsWith('product_'));
      productKeys.forEach(k => window.SessionState.clear(k));
      console.log('🧹 ProductDataCache: Cleared all cache', { count: productKeys.length });
    }
  }
  
  /**
   * Get cache statistics
   * 
   * @returns {object} Cache stats
   */
  static getStats() {
    if (!window.SessionState) {
      return { error: 'SessionState not available' };
    }
    
    const allKeys = window.SessionState.getAllKeys();
    const productKeys = allKeys.filter(k => k.startsWith('product_'));
    
    return {
      cachedProducts: productKeys.length,
      handles: productKeys.map(k => k.replace('product_', '')),
      sessionStats: window.SessionState.getStats()
    };
  }
}

/**
 * VariantSelector Service
 * 
 * Variant selection logic for ANY product (main product, module, or addon).
 * Works for both rental (date-based) and purchase (condition-based) variants.
 */
class VariantSelector {
  /**
   * Select rental variant based on dates
   * Works for ANY product (main, module, addon)
   * 
   * @param {string} productHandle - Product handle
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<object|null>} Matching variant or null if not found
   * 
   * @example
   * const variant = await VariantSelector.selectVariantForDates(
   *   'air-quality-monitor',
   *   '2024-03-15',
   *   '2024-03-20'
   * );
   * console.log(variant.id, variant.price);
   */
  static async selectVariantForDates(productHandle, startDate, endDate) {
    try {
      const days = this.calculateDays(startDate, endDate);
      const productData = await ProductDataCache.get(productHandle);
      
      // Find matching rental variant
      const targetDuration = `${days} Day`;
      const variant = productData.variants.find(v => 
        v.option1?.toLowerCase() === 'rent' && 
        v.option2 === targetDuration
      );
      
      if (!variant) {
        console.warn(`⚠️ VariantSelector: No variant found for "${productHandle}" with ${days} days`);
        console.warn(`   Looking for: Rent / ${targetDuration}`);
        console.warn(`   Available rental variants:`, 
          productData.variants
            .filter(v => v.option1?.toLowerCase() === 'rent')
            .map(v => v.option2)
        );
        return null;
      }
      
      console.log(`✅ VariantSelector: Found variant for "${productHandle}": ${variant.title} (${variant.id})`);
      return variant;
    } catch (error) {
      console.error(`❌ VariantSelector: Error selecting variant for "${productHandle}":`, error);
      return null;
    }
  }
  
  /**
   * Select purchase variant based on condition
   * 
   * @param {string} productHandle - Product handle
   * @param {string} condition - Condition ('New', 'Used', or null for first available)
   * @returns {Promise<object|null>} Matching variant or null if not found
   * 
   * @example
   * const variant = await VariantSelector.selectPurchaseVariant('air-quality-monitor', 'New');
   */
  static async selectPurchaseVariant(productHandle, condition = 'New') {
    try {
      const productData = await ProductDataCache.get(productHandle);
      
      // Find matching purchase variant
      const variant = productData.variants.find(v =>
        v.option1?.toLowerCase() === 'purchase' &&
        (condition === null || v.option2 === condition)
      );
      
      if (!variant) {
        console.warn(`⚠️ VariantSelector: No purchase variant found for "${productHandle}" with condition "${condition}"`);
        // Fallback to first variant
        return productData.variants[0] || null;
      }
      
      console.log(`✅ VariantSelector: Found purchase variant for "${productHandle}": ${variant.title}`);
      return variant;
    } catch (error) {
      console.error(`❌ VariantSelector: Error selecting purchase variant for "${productHandle}":`, error);
      return null;
    }
  }
  
  /**
   * Calculate rental days between two dates (inclusive)
   * 
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {number} Number of days (inclusive)
   * 
   * @example
   * const days = VariantSelector.calculateDays('2024-03-15', '2024-03-20');
   * console.log(days); // 6 (inclusive)
   */
  static calculateDays(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Ensure dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error('❌ VariantSelector: Invalid dates provided:', { startDate, endDate });
      return 0;
    }
    
    // Calculate difference in milliseconds
    const diffMs = end - start;
    
    // Convert to days (always round up partial days)
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    // Add 1 for inclusive counting (both start and end dates count as rental days)
    // Example: Mar 15 to Mar 16 = 1 day difference + 1 = 2 rental days
    const days = diffDays + 1;
    
    return days > 0 ? days : 0;
  }
  
  /**
   * Find variant by ID
   * 
   * @param {string} productHandle - Product handle
   * @param {number} variantId - Variant ID
   * @returns {Promise<object|null>} Variant or null if not found
   */
  static async findVariantById(productHandle, variantId) {
    try {
      const productData = await ProductDataCache.get(productHandle);
      const variant = productData.variants.find(v => v.id === variantId);
      
      if (!variant) {
        console.warn(`⚠️ VariantSelector: Variant ${variantId} not found for "${productHandle}"`);
      }
      
      return variant || null;
    } catch (error) {
      console.error(`❌ VariantSelector: Error finding variant ${variantId}:`, error);
      return null;
    }
  }
}

/**
 * PriceCalculator Service
 * 
 * Price fetching and calculations for any item.
 * Uses VariantSelector internally for date-based pricing.
 */
class PriceCalculator {
  /**
   * Get price for specific duration
   * Works for rentals and purchases
   * Uses SessionState for price caching
   * 
   * @param {string} productHandle - Product handle
   * @param {string} duration - Duration ('7 Day', 'Purchase', etc.)
   * @returns {Promise<number|null>} Price in dollars or null if not found
   * 
   * @example
   * const price = await PriceCalculator.getPriceForDuration('air-quality-monitor', '7 Day');
   * console.log(`$${price.toFixed(2)}`);
   */
  static async getPriceForDuration(productHandle, duration) {
    // Check price cache first
    const priceCacheKey = `price_${productHandle}_${duration}`;
    const cachedPrice = window.SessionState?.get(priceCacheKey);
    
    if (cachedPrice !== null) {
      console.log(`📦 PriceCalculator: Using cached price for "${productHandle}" (${duration}): $${cachedPrice.toFixed(2)}`);
      return cachedPrice;
    }
    
    try {
      const productData = await ProductDataCache.get(productHandle);
      
      // Find matching variant
      const variant = productData.variants.find(v =>
        // Rental: Rent / {duration}
        (v.option1?.toLowerCase() === 'rent' && v.option2 === duration) ||
        // Simple rental: {duration} only
        (v.option1 === duration) ||
        // Purchase: Purchase / {condition}
        (v.option1?.toLowerCase() === 'purchase' && duration === 'Purchase')
      );
      
      if (!variant) {
        console.warn(`⚠️ PriceCalculator: No variant found for "${productHandle}" with duration "${duration}"`);
        return null;
      }
      
      // Convert from cents to dollars
      const price = variant.price / 100;
      
      // Cache the price result
      if (window.SessionState) {
        window.SessionState.set(priceCacheKey, price);
      }
      
      console.log(`✅ PriceCalculator: Price for "${productHandle}" (${duration}): $${price.toFixed(2)}`);
      
      return price;
    } catch (error) {
      console.error(`❌ PriceCalculator: Error getting price for "${productHandle}":`, error);
      return null;
    }
  }
  
  /**
   * Get price for date range (uses VariantSelector internally)
   * 
   * @param {string} productHandle - Product handle
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<number|null>} Price in dollars or null if not found
   * 
   * @example
   * const price = await PriceCalculator.getPriceForDates(
   *   'air-quality-monitor',
   *   '2024-03-15',
   *   '2024-03-20'
   * );
   */
  static async getPriceForDates(productHandle, startDate, endDate) {
    try {
      const variant = await VariantSelector.selectVariantForDates(
        productHandle, 
        startDate, 
        endDate
      );
      
      if (!variant) {
        return null;
      }
      
      // Convert from cents to dollars
      const price = variant.price / 100;
      return price;
    } catch (error) {
      console.error(`❌ PriceCalculator: Error getting price for dates:`, error);
      return null;
    }
  }
  
  /**
   * Get variant with price for duration
   * Returns both variant and price for efficiency
   * 
   * @param {string} productHandle - Product handle
   * @param {string} duration - Duration
   * @returns {Promise<object|null>} Object with variant and price, or null
   */
  static async getVariantAndPrice(productHandle, duration) {
    try {
      const productData = await ProductDataCache.get(productHandle);
      
      const variant = productData.variants.find(v =>
        (v.option1?.toLowerCase() === 'rent' && v.option2 === duration) ||
        (v.option1 === duration) ||
        (v.option1?.toLowerCase() === 'purchase' && duration === 'Purchase')
      );
      
      if (!variant) {
        return null;
      }
      
      return {
        variant,
        price: variant.price / 100
      };
    } catch (error) {
      console.error(`❌ PriceCalculator: Error getting variant and price:`, error);
      return null;
    }
  }
  
  /**
   * Clear price cache for specific product/duration or all prices
   * 
   * @param {string} [productHandle] - Optional product handle
   * @param {string} [duration] - Optional duration
   * 
   * @example
   * PriceCalculator.clearCache('air-quality-monitor', '7 Day'); // Clear specific price
   * PriceCalculator.clearCache('air-quality-monitor'); // Clear all prices for product
   * PriceCalculator.clearCache(); // Clear all prices
   */
  static clearCache(productHandle = null, duration = null) {
    if (!window.SessionState) return;
    
    if (productHandle && duration) {
      // Clear specific price
      const cacheKey = `price_${productHandle}_${duration}`;
      window.SessionState.clear(cacheKey);
      console.log('🧹 PriceCalculator: Cleared price cache', { productHandle, duration });
    } else if (productHandle) {
      // Clear all prices for a product
      const allKeys = window.SessionState.getAllKeys();
      const productPriceKeys = allKeys.filter(k => k.startsWith(`price_${productHandle}_`));
      productPriceKeys.forEach(k => window.SessionState.clear(k));
      console.log('🧹 PriceCalculator: Cleared all prices for product', { productHandle, count: productPriceKeys.length });
    } else {
      // Clear all price cache
      const allKeys = window.SessionState.getAllKeys();
      const priceKeys = allKeys.filter(k => k.startsWith('price_'));
      priceKeys.forEach(k => window.SessionState.clear(k));
      console.log('🧹 PriceCalculator: Cleared all price cache', { count: priceKeys.length });
    }
  }
}

/**
 * CartManager Service
 * 
 * All cart operations with automatic event emission.
 * Automatically emits CART_UPDATED event after cart modifications.
 */
class CartManager {
  /**
   * Update cart attributes (dates, PO, etc.)
   * Automatically emits CART_UPDATED event
   * 
   * @param {object} attributes - Attributes to update
   * @returns {Promise<object>} Updated cart object
   * 
   * @example
   * await CartManager.updateAttributes({
   *   'Rental Start Date': '2024-03-15',
   *   'Rental End Date': '2024-03-20'
   * });
   */
  static async updateAttributes(attributes) {
    try {
      console.log('🛒 CartManager: Updating cart attributes:', attributes);
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const cart = await response.json();
      console.log('✅ CartManager: Cart attributes updated');
      
      // Emit CART_UPDATED event
      if (window.eventBus) {
        window.eventBus.emit('CART_UPDATED', { cart });
      }
      
      return cart;
    } catch (error) {
      console.error('❌ CartManager: Error updating cart attributes:', error);
      throw error;
    }
  }
  
  /**
   * Add item to cart
   * 
   * @param {number} variantId - Variant ID
   * @param {number} quantity - Quantity
   * @param {object} properties - Line item properties
   * @returns {Promise<object>} Add to cart response
   * 
   * @example
   * await CartManager.addItem(12345, 1, {
   *   'Rental Start Date': '2024-03-15',
   *   '_module_type': 'required'
   * });
   */
  static async addItem(variantId, quantity = 1, properties = {}) {
    try {
      console.log(`🛒 CartManager: Adding item ${variantId} (qty: ${quantity})`);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: variantId, quantity, properties }]
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('✅ CartManager: Item added to cart');
      
      return result;
    } catch (error) {
      console.error('❌ CartManager: Error adding item to cart:', error);
      throw error;
    }
  }
  
  /**
   * Swap item variant (remove old, add new)
   * Used when dates change and variants need to be updated
   * 
   * @param {string} itemKey - Cart item key
   * @param {number} newVariantId - New variant ID
   * @param {number} quantity - Quantity
   * @param {object} properties - Line item properties
   * @returns {Promise<void>}
   * 
   * @example
   * await CartManager.swapItemVariant(
   *   '12345:abcd',
   *   67890,
   *   2,
   *   { 'Rental Start Date': '2024-03-20' }
   * );
   */
  static async swapItemVariant(itemKey, newVariantId, quantity, properties = {}) {
    try {
      console.log(`🛒 CartManager: Swapping item ${itemKey} to variant ${newVariantId}`);
      
      // Remove old variant
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });
      
      // Add new variant
      await this.addItem(newVariantId, quantity, properties);
      
      console.log('✅ CartManager: Item variant swapped');
    } catch (error) {
      console.error('❌ CartManager: Error swapping item variant:', error);
      throw error;
    }
  }
  
  /**
   * Get current cart
   * 
   * @returns {Promise<object>} Current cart object
   * 
   * @example
   * const cart = await CartManager.getCart();
   * console.log(cart.item_count, cart.items);
   */
  static async getCart() {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('❌ CartManager: Error getting cart:', error);
      throw error;
    }
  }
  
  /**
   * Clear entire cart
   * 
   * @returns {Promise<object>} Empty cart object
   */
  static async clearCart() {
    try {
      console.log('🛒 CartManager: Clearing cart');
      
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const cart = await response.json();
      console.log('✅ CartManager: Cart cleared');
      
      // Emit CART_UPDATED event
      if (window.eventBus) {
        window.eventBus.emit('CART_UPDATED', { cart });
      }
      
      return cart;
    } catch (error) {
      console.error('❌ CartManager: Error clearing cart:', error);
      throw error;
    }
  }
}

// Make services available globally
window.ProductDataCache = ProductDataCache;
window.VariantSelector = VariantSelector;
window.PriceCalculator = PriceCalculator;
window.CartManager = CartManager;

console.log('✅ Services initialized and available globally', {
  caching: window.SessionState ? 'SessionState (persistent)' : 'Memory (page-only)',
  services: ['ProductDataCache', 'VariantSelector', 'PriceCalculator', 'CartManager']
});

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    ProductDataCache,
    VariantSelector,
    PriceCalculator,
    CartManager
  };
}
